import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { config } from '../config/environment';
import {
  FoundObject,
  CreateFoundObjectRequest,
  UpdateFoundObjectRequest,
  FoundObjectStats,
  ProviderInfo,
  ProviderEventRow,
} from '../types/foundObject';
import type { ProviderPlaceItem } from '../types/providerPlace';
import type { OpeningHours } from '../utils/providerPlaceOpeningHours';
import type { ProviderStorageBoxRow } from '../types/storageBox';
import { clearAllStoredProviderPlaces } from '../utils/providerDashboardPlace';
import {
  compressImage,
  compressImageTowardsTargetBytes,
  COMPRESSION_PRESETS,
  isImageFile,
} from '../utils/imageCompression';
import { sortCategoriesByName } from '../utils/sortCategoriesByName';
import type { DeliveryPricingResponse } from '../utils/deliveryPricing';
import {
  browserStorageGet,
  browserStorageRemove,
  browserStorageSet,
  isBrowserStorageAvailable,
} from '../utils/browserStorage';

const API_BASE_URL = config.apiUrl.replace(/\/$/, '');

/** En-tête HTTP lu par l’API pour le contexte partenaire (super-admin). */
export const PROVIDER_DASHBOARD_CONTEXT_HEADER = 'X-Provider-Context-Id';

/** Inbox messagerie partenaire uniquement (exclut les conversations personnelles Sherlook). */
export const PROVIDER_MESSAGING_SCOPE_HEADER = 'X-Sherlook-Messaging-Scope';
export const PROVIDER_MESSAGING_SCOPE_VALUE = 'provider';

export const PROVIDER_DASHBOARD_CONTEXT_STORAGE_KEY = 'sherlook_provider_dashboard_context_id';

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      headers: { 'Content-Type': 'application/json' },
    });

    this.api.interceptors.request.use((req) => {
      const token = browserStorageGet('auth_token');
      if (token) {
        req.headers['X-Auth-Token'] = token;
      }
      req.headers[PROVIDER_MESSAGING_SCOPE_HEADER] = PROVIDER_MESSAGING_SCOPE_VALUE;
      const ctx = browserStorageGet(PROVIDER_DASHBOARD_CONTEXT_STORAGE_KEY);
      if (ctx && this.isCurrentUserSuperAdmin()) {
        req.headers[PROVIDER_DASHBOARD_CONTEXT_HEADER] = ctx;
      } else if (!this.isCurrentUserSuperAdmin()) {
        try {
          const userStr = browserStorageGet('user_data');
          const user = userStr ? JSON.parse(userStr) : null;
          const providerId = user?.location_provider?.id;
          if (providerId) {
            req.headers[PROVIDER_DASHBOARD_CONTEXT_HEADER] = String(providerId);
          }
        } catch {
          // ignore
        }
      }
      return req;
    });

    this.api.interceptors.response.use(
      (res) => res,
      (error) => {
        if (error.response?.status === 400 && error.response?.data?.code === 'PROVIDER_CONTEXT_REQUIRED') {
          if (typeof window !== 'undefined' && this.isCurrentUserSuperAdmin()) {
            const p = window.location.pathname || '';
            if (!p.includes('/select-provider') && !p.includes('/login')) {
              window.location.href = '/select-provider';
            }
          }
        }
        if (error.response?.status === 401) {
          browserStorageRemove('auth_token');
          browserStorageRemove('user_data');
          browserStorageRemove(PROVIDER_DASHBOARD_CONTEXT_STORAGE_KEY);
          clearAllStoredProviderPlaces();
          if (typeof window !== 'undefined' && !window.location.pathname.includes('/login')) {
            window.location.href = '/login';
          }
        }
        return Promise.reject(error);
      }
    );
  }

  private getAuthToken(): string | null {
    return browserStorageGet('auth_token');
  }

  // Found Objects
  async getFoundObjects(params?: {
    limit?: number;
    offset?: number;
    search?: string;
    referenceCode?: string;
    category?: string;
    status?: string;
    dateFrom?: string;
    dateTo?: string;
    createdFrom?: string;
    createdTo?: string;
    location?: string;
    attributes?: Record<string, string>;
    provider_event_id?: number;
    storage_box_id?: number;
    provider_item_id?: number;
    hasConversations?: boolean;
  }): Promise<
    AxiosResponse<{
      data: {
        found_objects: FoundObject[];
        total: number;
        page: number;
        limit: number;
        total_pages: number;
      };
    }>
  > {
    const queryParams: Record<string, unknown> = {
      limit: params?.limit,
      offset: params?.offset,
      search: params?.search,
      reference_code: params?.referenceCode,
      category: params?.category,
      status: params?.status,
      date_from: params?.dateFrom,
      date_to: params?.dateTo,
      created_from: params?.createdFrom,
      created_to: params?.createdTo,
      location: params?.location,
      provider_event_id: params?.provider_event_id,
      storage_box_id: params?.storage_box_id,
      provider_item_id: params?.provider_item_id,
      has_conversations: params?.hasConversations === true ? '1' : undefined,
    };

    if (params?.attributes) {
      Object.entries(params.attributes).forEach(([key, value]) => {
        queryParams[`attributes[${key}]`] = value;
      });
    }

    Object.keys(queryParams).forEach((key) => {
      const v = queryParams[key];
      if (v === undefined || v === '') {
        delete queryParams[key];
      }
    });

    return this.api.get('/found-objects', { params: queryParams });
  }

  async getFoundObject(id: number): Promise<AxiosResponse<{ data: FoundObject }>> {
    return this.api.get(`/found-objects/${id}`);
  }

  async sendAccountlessDeliveryOffer(
    id: number,
    payload: {
      first_name: string;
      last_name: string;
      email: string;
      carrier: string;
      amount_cents: number;
      message?: string;
    }
  ): Promise<
    AxiosResponse<{
      message: string;
      conversation_id: number;
      owner_user_id: number;
      message_id: number;
      found_object: FoundObject;
    }>
  > {
    return this.api.post(`/found-objects/${id}/accountless-delivery-offer`, payload);
  }

  async createFoundObject(
    data: CreateFoundObjectRequest
  ): Promise<AxiosResponse<{ message: string; found_object: FoundObject }>> {
    return this.api.post('/found-objects', data);
  }

  async updateFoundObject(
    id: number,
    data: UpdateFoundObjectRequest
  ): Promise<AxiosResponse<{ message: string; found_object: FoundObject }>> {
    return this.api.put(`/found-objects/${id}`, data);
  }

  async deleteFoundObject(id: number): Promise<AxiosResponse<void>> {
    return this.api.delete(`/found-objects/${id}`);
  }

  /** Réponse API : objet plat { total, available, claimed, … } (pas de clé `data`). */
  async getFoundObjectStats(): Promise<AxiosResponse<FoundObjectStats>> {
    return this.api.get('/found-objects/stats');
  }

  async getProviderEvents(locationProviderId?: number): Promise<
    AxiosResponse<{ provider_events: ProviderEventRow[]; total: number }>
  > {
    const params =
      locationProviderId != null ? { location_provider_id: locationProviderId } : undefined;
    return this.api.get('/provider/events', { params });
  }

  async createProviderEvent(data: {
    name: string;
    year: number;
    starts_at: string;
    ends_at: string;
    is_active?: boolean;
    slug?: string;
    location_provider_id?: number;
  }): Promise<AxiosResponse<ProviderEventRow>> {
    return this.api.post('/provider/events', data);
  }

  async updateProviderEvent(
    id: number,
    data: Partial<{
      name: string;
      year: number;
      starts_at: string;
      ends_at: string;
      is_active: boolean;
      slug: string;
    }>
  ): Promise<AxiosResponse<ProviderEventRow>> {
    return this.api.put(`/provider/events/${id}`, data);
  }

  async deleteProviderEvent(id: number): Promise<AxiosResponse<{ message: string }>> {
    return this.api.delete(`/provider/events/${id}`);
  }

  // Authentication
  async login(identifier: string, password: string): Promise<AxiosResponse<{ token: string; user: any }>> {
    if (!isBrowserStorageAvailable()) {
      const err = new Error(
        'Stockage navigateur bloqué. Autorisez les cookies / données du site pour dashboard.sherlook.fr (Ecosia : bouclier → autoriser le site).'
      ) as Error & { response?: { data: { error: string } } };
      err.response = { data: { error: err.message } };
      throw err;
    }
    const response = await axios.post(`${API_BASE_URL}/login`, { identifier, password });
    if (response.data.token) {
      const tokenOk = browserStorageSet('auth_token', response.data.token);
      const userOk = browserStorageSet('user_data', JSON.stringify(response.data.user));
      if (!tokenOk || !userOk) {
        const err = new Error(
          'Impossible d’enregistrer la session. Autorisez le stockage du site (Ecosia / mode privé).'
        ) as Error & { response?: { data: { error: string } } };
        err.response = { data: { error: err.message } };
        throw err;
      }
      this.clearProviderDashboardContext();
      clearAllStoredProviderPlaces();
    }
    return response;
  }

  async getProviderForgotPasswordOptions(email: string): Promise<
    AxiosResponse<{ providers: Array<{ id: number; name: string; type?: string | null }> }>
  > {
    return axios.post(`${API_BASE_URL}/provider-forgot-password/options`, { email });
  }

  async forgotPassword(email: string, providerId?: number): Promise<AxiosResponse<{ message: string }>> {
    const payload: { email: string; provider_id?: number } = { email };
    if (providerId) {
      payload.provider_id = providerId;
    }
    return axios.post(`${API_BASE_URL}/forgot-password`, payload);
  }

  async resetPassword(token: string, password: string): Promise<AxiosResponse<{ message: string }>> {
    return axios.post(`${API_BASE_URL}/reset-password`, {
      token,
      password,
      reset_origin: 'provider_dashboard',
    });
  }

  async listProvidersForSuperAdmin(params?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
  }): Promise<
    AxiosResponse<{
      providers: Array<{ id: number; name: string; type?: string | null; status?: string | null }>;
      pagination: { page: number; limit: number; total: number; pages: number };
    }>
  > {
    return this.api.get('/providers', {
      params: {
        page: params?.page ?? 1,
        limit: params?.limit ?? 100,
        search: params?.search,
        status: params?.status,
      },
    });
  }

  async getProviderInfo(): Promise<AxiosResponse<ProviderInfo>> {
    return this.api.get('/providers/provider/info');
  }

  async updateProviderInfo(data: any): Promise<AxiosResponse<{ data: any }>> {
    return this.api.put('/provider/info', data);
  }

  async updateProvider(providerId: number, data: any): Promise<AxiosResponse<any>> {
    return this.api.put(`/providers/${providerId}`, data);
  }

  async getCategories(): Promise<AxiosResponse<{ categories: any[] }>> {
    const params: { location_provider_id?: number } = {};
    const contextId = this.getProviderDashboardContextId();
    if (contextId) {
      params.location_provider_id = contextId;
    } else {
      try {
        const userStr = browserStorageGet('user_data');
        const user = userStr ? JSON.parse(userStr) : null;
        const providerId = user?.location_provider?.id;
        if (providerId) {
          params.location_provider_id = Number(providerId);
        }
      } catch {
        // ignore
      }
    }

    const response = await this.api.get<{ categories: any[] }>('/categories', {
      params: Object.keys(params).length > 0 ? params : undefined,
    });
    if (response.data?.categories) {
      response.data.categories = sortCategoriesByName(response.data.categories);
    }
    return response;
  }

  async getCategory(id: number): Promise<AxiosResponse<Record<string, unknown>>> {
    return this.api.get(`/categories/${id}`);
  }

  /**
   * Upload image (multipart via fetch pour éviter Content-Type: application/json du client Axios).
   * @param scope `messages` → stockage sous `/uploads/messages/` (chat), défaut `announcements`.
   */
  async uploadImage(
    file: File,
    scope: 'announcements' | 'messages' = 'announcements'
  ): Promise<
    AxiosResponse<{
      filename: string;
      url: string;
      path?: string;
      size: number;
      mime_type: string;
      success?: boolean;
      scope?: string;
    }>
  > {
    let fileToSend = file;
    try {
      if (isImageFile(file)) {
        if (scope === 'announcements') {
          fileToSend = (await compressImageTowardsTargetBytes(file)).file;
        } else {
          fileToSend = (await compressImage(file, COMPRESSION_PRESETS.medium)).file;
        }
      }
    } catch {
      /* upload du fichier original si la compression échoue */
    }

    const formData = new FormData();
    formData.append('image', fileToSend);
    if (scope !== 'announcements') {
      formData.append('scope', scope);
    }
    const token = this.getAuthToken();
    const headers: Record<string, string> = {};
    if (token) {
      headers['X-Auth-Token'] = token;
    }
    const ctx = browserStorageGet(PROVIDER_DASHBOARD_CONTEXT_STORAGE_KEY);
    if (ctx && this.isCurrentUserSuperAdmin()) {
      headers[PROVIDER_DASHBOARD_CONTEXT_HEADER] = ctx;
    }
    const res = await fetch(`${API_BASE_URL}/upload/image`, {
      method: 'POST',
      headers,
      body: formData,
    });
    const data = (await res.json().catch(() => ({}))) as {
      filename?: string;
      url?: string;
      path?: string;
      error?: string;
    };
    if (!res.ok) {
      const err = new Error(String(data.error || `Upload échoué (${res.status})`)) as Error & {
        response?: { status: number; data: typeof data };
      };
      err.response = { status: res.status, data };
      throw err;
    }
    return {
      data: data as {
        filename: string;
        url: string;
        path?: string;
        size: number;
        mime_type: string;
        success?: boolean;
        scope?: string;
      },
      status: res.status,
      statusText: res.statusText,
      headers: {} as any,
      config: {} as any,
    };
  }

  isAuthenticated(): boolean {
    const token = this.getAuthToken();
    if (!token) return false;
    try {
      const tokenData = JSON.parse(atob(token));
      return !!(tokenData.exp && tokenData.exp > Date.now() / 1000);
    } catch {
      return false;
    }
  }

  getCurrentUser(): any {
    const token = this.getAuthToken();
    if (!token) return null;
    try {
      const tokenData = JSON.parse(atob(token));
      const userStr = browserStorageGet('user_data');
      const fromStorage = userStr ? JSON.parse(userStr) : null;
      return {
        ...tokenData,
        ...fromStorage,
        roles: tokenData.roles ?? fromStorage?.roles,
      };
    } catch {
      return null;
    }
  }

  getCurrentUserId(): number | null {
    const user = this.getCurrentUser();
    const raw = user?.id ?? user?.user_id;
    const id = typeof raw === 'number' ? raw : Number.parseInt(String(raw ?? ''), 10);
    return Number.isFinite(id) && id > 0 ? id : null;
  }

  private getUserRolesArray(): string[] {
    const user = this.getCurrentUser();
    const raw = user?.roles;
    if (Array.isArray(raw)) {
      return raw;
    }
    if (typeof raw === 'string') {
      try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    return [];
  }

  isCurrentUserSuperAdmin(): boolean {
    return this.getUserRolesArray().includes('ROLE_SUPER_ADMIN');
  }

  getProviderDashboardContextId(): number | null {
    const v = browserStorageGet(PROVIDER_DASHBOARD_CONTEXT_STORAGE_KEY);
    if (!v) return null;
    const n = parseInt(v, 10);
    return Number.isFinite(n) && n > 0 ? n : null;
  }

  setProviderDashboardContextId(providerId: number): void {
    browserStorageSet(PROVIDER_DASHBOARD_CONTEXT_STORAGE_KEY, String(providerId));
  }

  clearProviderDashboardContext(): void {
    browserStorageRemove(PROVIDER_DASHBOARD_CONTEXT_STORAGE_KEY);
  }

  /** Rôles autorisés par l’API sur les routes partenaire (ex. /found-objects). */
  canAccessProviderDashboard(): boolean {
    if (!this.isAuthenticated()) return false;
    const roles = this.getUserRolesArray();
    const allowed = ['ROLE_PROVIDER', 'ROLE_LOCATION_ADMIN', 'ROLE_SUPER_ADMIN'];
    return allowed.some((r) => roles.includes(r));
  }

  logout(): void {
    browserStorageRemove('auth_token');
    browserStorageRemove('user_data');
    browserStorageRemove(PROVIDER_DASHBOARD_CONTEXT_STORAGE_KEY);
    clearAllStoredProviderPlaces();
  }

  async getProviderTemplate(type: string): Promise<AxiosResponse<any>> {
    return this.api.get(`/provider-templates/${type}`);
  }

  /** Lieux / sous-espaces du partenaire connecté (ProviderItem) */
  async getProviderItems(providerId: number): Promise<
    AxiosResponse<{
      provider_items: ProviderPlaceItem[];
      total: number;
    }>
  > {
    return this.api.get(`/provider-items/provider/${providerId}`);
  }

  async createProviderItem(
    providerId: number,
    data: {
      name: string;
      description?: string;
      attributes?: Record<string, unknown>;
      sort_order?: number;
      is_active?: boolean;
    }
  ): Promise<AxiosResponse<ProviderPlaceItem>> {
    return this.api.post(`/provider-items/provider/${providerId}`, data);
  }

  async updateProviderItem(
    id: number,
    data: {
      name?: string;
      description?: string;
      attributes?: Record<string, unknown>;
      sort_order?: number;
      is_active?: boolean;
      opening_hours?: OpeningHours;
    }
  ): Promise<AxiosResponse<ProviderPlaceItem>> {
    return this.api.put(`/provider-items/${id}`, data);
  }

  async deleteProviderItem(id: number): Promise<AxiosResponse<{ message: string }>> {
    return this.api.delete(`/provider-items/${id}`);
  }

  async toggleProviderItemStatus(id: number): Promise<AxiosResponse<{ is_active: boolean }>> {
    return this.api.patch(`/provider-items/${id}/toggle-status`);
  }

  /** Boîtes de stockage rattachées aux lieux (ProviderItem). */
  async getStorageBoxesByProvider(providerId: number): Promise<
    AxiosResponse<{ storage_boxes: ProviderStorageBoxRow[]; total: number }>
  > {
    return this.api.get(`/provider-storage-boxes/provider/${providerId}`);
  }

  async getStorageBoxesByPlace(providerItemId: number): Promise<
    AxiosResponse<{ storage_boxes: ProviderStorageBoxRow[]; total: number }>
  > {
    return this.api.get(`/provider-storage-boxes/place/${providerItemId}`);
  }

  async createStorageBox(
    providerItemId: number,
    data: {
      category_id?: number;
      name?: string;
      description?: string;
      sort_order?: number;
      is_active?: boolean;
    }
  ): Promise<AxiosResponse<ProviderStorageBoxRow>> {
    return this.api.post(`/provider-storage-boxes/place/${providerItemId}`, data);
  }

  async createCentralStorageBox(
    providerId: number,
    data: {
      category_id?: number;
      name?: string;
      description?: string;
      sort_order?: number;
      is_active?: boolean;
    }
  ): Promise<AxiosResponse<ProviderStorageBoxRow>> {
    return this.api.post(`/provider-storage-boxes/provider/${providerId}`, data);
  }

  async updateStorageBox(
    id: number,
    data: { name?: string; description?: string | null; sort_order?: number; is_active?: boolean }
  ): Promise<AxiosResponse<ProviderStorageBoxRow>> {
    return this.api.put(`/provider-storage-boxes/${id}`, data);
  }

  async deleteStorageBox(id: number): Promise<AxiosResponse<{ message: string }>> {
    return this.api.delete(`/provider-storage-boxes/${id}`);
  }

  // ===== Matchings (comme sherlook-frontend-next) =====

  async getUserMatches(filters?: {
    status?: string;
    exclude_status?: string;
    page?: number;
    limit?: number;
  }): Promise<{ matches: any[]; pagination: any }> {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.exclude_status) params.append('exclude_status', filters.exclude_status);
    if (filters?.page) params.append('page', String(filters.page));
    if (filters?.limit) params.append('limit', String(filters.limit));

    const response = await this.api.get<{ matches: any[]; pagination: any }>(`/matches/my?${params.toString()}`);
    const raw = response.data.matches ?? [];
    const matches = raw.map((m: Record<string, unknown>) => {
      const score = m.score ?? m.match_score;
      const reason = m.reason ?? m.match_reason ?? null;
      return {
        ...m,
        score,
        reason,
        match_score: score,
        match_reason: reason,
      };
    });
    return { ...response.data, matches };
  }

  async confirmMatch(id: number): Promise<void> {
    await this.api.post(`/matches/${id}/confirm`);
  }

  async unconfirmMatch(id: number): Promise<void> {
    await this.api.post(`/matches/${id}/unconfirm`);
  }

  async rejectMatch(id: number): Promise<void> {
    await this.api.post(`/matches/${id}/reject`);
  }

  async unrejectMatch(id: number): Promise<void> {
    await this.api.post(`/matches/${id}/unreject`);
  }

  async resolveMatch(id: number): Promise<void> {
    await this.api.post(`/matches/${id}/resolve`);
  }

  // ===== Conversations / messagerie =====

  async getConversations(options?: {
    archived?: boolean;
    restored?: boolean;
    all?: boolean;
  }): Promise<{ conversations: any[]; total_unread: number; archived?: boolean; restored?: boolean; all?: boolean }> {
    const params = new URLSearchParams();
    if (options?.all) {
      params.set('all', '1');
    } else if (options?.archived) {
      params.set('archived', '1');
    } else if (options?.restored) {
      params.set('restored', '1');
    }
    const qs = params.toString();
    const response = await this.api.get<{
      conversations: any[];
      total_unread: number;
      archived?: boolean;
      restored?: boolean;
      all?: boolean;
    }>(`/conversations${qs ? `?${qs}` : ''}`);
    return response.data;
  }

  async getConversationsUnreadCount(): Promise<{ count: number }> {
    const response = await this.api.get<{ count: number }>('/conversations/unread-count');
    return response.data;
  }

  async getConversation(
    id: number,
    page = 1,
    limit = 50,
    options?: { markRead?: boolean }
  ): Promise<{
    conversation: any;
    messages: any[];
    pagination: any;
  }> {
    const params = new URLSearchParams({
      page: String(page),
      limit: String(limit),
    });
    if (options?.markRead === false) {
      params.set('mark_read', '0');
    }
    const response = await this.api.get<{ conversation: any; messages: any[]; pagination: any }>(
      `/conversations/${id}?${params.toString()}`
    );
    return response.data;
  }

  async archiveConversation(id: number): Promise<{ message: string; conversation_id: number; is_archived: boolean }> {
    const response = await this.api.post(`/conversations/${id}/archive`);
    return response.data;
  }

  async unarchiveConversation(id: number): Promise<{ message: string; conversation_id: number; is_archived: boolean }> {
    const response = await this.api.post(`/conversations/${id}/unarchive`);
    return response.data;
  }

  async markConversationUnread(id: number): Promise<{ message: string; conversation_id: number; unread_count: number }> {
    const response = await this.api.post(`/conversations/${id}/mark-unread`);
    return response.data;
  }

  async markConversationTreated(id: number): Promise<{
    message: string;
    conversation_id: number;
    pending_reply: boolean;
  }> {
    const response = await this.api.post(`/conversations/${id}/mark-treated`);
    return response.data;
  }

  async sendMessage(conversationId: number, content: string): Promise<{ message: any }> {
    const response = await this.api.post<{ message: any }>(`/conversations/${conversationId}/messages`, {
      content,
    });
    return response.data;
  }

  /** Presence : conversation ouverte dans le dashboard (désactive l’auto-accusé répondeur). */
  async pingConversationViewing(conversationId: number, viewing = true): Promise<{ ok: boolean }> {
    const response = await this.api.post<{ ok: boolean }>(`/conversations/${conversationId}/typing`, {
      typing: viewing,
      viewing: true,
    });
    return response.data;
  }

  async updateConversationMessage(
    conversationId: number,
    messageId: number,
    content: string
  ): Promise<{ message: any }> {
    const response = await this.api.patch<{ message: any }>(
      `/conversations/${conversationId}/messages/${messageId}`,
      { content }
    );
    return response.data;
  }

  async deleteConversationMessage(
    conversationId: number,
    messageId: number
  ): Promise<{ message: string; data?: any }> {
    const response = await this.api.delete<{ message: string; data?: any }>(
      `/conversations/${conversationId}/messages/${messageId}`
    );
    return response.data;
  }

  async startConversation(
    announcementId: number,
    content?: string
  ): Promise<{ conversation_id: number; message: any | null }> {
    const payload: { announcement_id: number; content?: string } = { announcement_id: announcementId };
    if (content != null && content.trim() !== '') {
      payload.content = content;
    }
    const response = await this.api.post<{ conversation_id: number; message: any | null }>(
      '/conversations/start',
      payload
    );
    return response.data;
  }

  async getDeliveryPricing(params?: {
    conversationId?: number;
    locationProviderId?: number;
  }): Promise<DeliveryPricingResponse> {
    const response = await this.api.get<DeliveryPricingResponse>('/delivery/pricing', {
      params: {
        ...(params?.conversationId ? { conversation_id: params.conversationId } : {}),
        ...(params?.locationProviderId ? { location_provider_id: params.locationProviderId } : {}),
      },
    });
    return response.data;
  }

  async createColissimoCheckoutSession(params: {
    conversationId: number;
    addressLine: string;
    postalCode: string;
    city: string;
  }): Promise<{ checkoutUrl?: string; sessionId?: string }> {
    const response = await this.api.post<{ checkoutUrl?: string; sessionId?: string }>(
      '/delivery/colissimo/checkout',
      {
        conversation_id: params.conversationId,
        shipping_method: 'colissimo',
        address_line: params.addressLine,
        postal_code: params.postalCode,
        city: params.city,
      }
    );
    return response.data;
  }

  /**
   * Annonces « perdu chez un partenaire » liées à l’établissement du compte connecté (ROLE_PROVIDER).
   */
  async getLinkedUserAnnouncements(params?: { page?: number; limit?: number }): Promise<{
    announcements: Array<{
      id: number;
      title: string;
      description: string;
      type: string;
      status: string;
      is_public?: boolean;
      created_at: string;
      user: { id: number; first_name: string; last_name: string };
      category: { name: string };
      location: { city?: string };
    }>;
    pagination: { page: number; limit: number; total: number; pages: number };
  }> {
    const p = new URLSearchParams();
    if (params?.page) p.set('page', String(params.page));
    if (params?.limit) p.set('limit', String(params.limit));
    const response = await this.api.get(`/announcements/for-provider?${p.toString()}`);
    return response.data;
  }
}

const apiService = new ApiService();
export default apiService;
